import { useEffect, useState } from "react";
import { sunboundPayProxy } from '@proxies';
import { useSpinnerContext } from '@contexts';
import { Constants, Enums } from "@shared";
import { useSessionContext } from '@contexts';
import { useNavigate } from 'react-router-dom';
import Statement from './statement';
import { Button } from '@common';
import type { Invoice } from '@app-types';

interface InvoiceWithState extends Invoice {
    open?: boolean;
}

const PaymentHistory = () => {

    const { setSpinnerShow } = useSpinnerContext();
    const [invoices, setInvoices] = useState<InvoiceWithState[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    const { session } = useSessionContext();
    const { idAccount } = session || {};
    const navigate = useNavigate();

    useEffect(() => {
        (async () => {
            if (!idAccount) return;
            setSpinnerShow(true);
            setIsLoading(true);
            try {
                const response = await sunboundPayProxy.getPayments(idAccount);
                if (response && response.length !== 0)
                    response[0].open = true;
                setInvoices(response);
            } catch (error) {
                console.error(error);
                setHasError(true);
            }
            setIsLoading(false);
            setSpinnerShow(false);
        })();
    }, []);

    const toggle = (index: number) => {
        const newHistory = [...invoices];
        const invoice = newHistory[index];
        if (!invoice) return; // For TS compiler; index should always be in range
        invoice.open = !invoice.open;
        setInvoices(newHistory);
    };

    const download = (idInvoice: number) => {
        if (!idAccount) return;
        // Open PDF directly in new tab - browser will handle display
        const url = sunboundPayProxy.getInvoiceDocumentUrl(idAccount, idInvoice);
        window.open(url, `_blank`);
    };

    return (
        <div className="flex flex-col w-full">
            {/* Header */}
            <div className="border-b border-neutral-100 pb-6 mb-8">
                <h1 className="text-[20px] leading-[24px] md:text-[28px] md:leading-[40px] font-medium text-neutral-950 tracking-[0.2px] md:tracking-[0.28px]">
                    Statements
                </h1>
            </div>

            {hasError ? (
                /* Error State */
                <div className="flex flex-col gap-4">
                    <p className="text-base leading-6 text-neutral-800 tracking-[0.16px]">
                        An error occurred while loading your statements.
                    </p>
                    <p className="text-base leading-6 text-neutral-800 tracking-[0.16px]">
                        Please try again later or{` `}
                        <a
                            href={`mailto:${Constants.Help.Email}`}
                            className="text-blue-800 focus-visible:outline-focus-ring rounded"
                        >
                            contact support
                        </a>
                        {` `}for assistance.
                    </p>
                </div>
            ) : isLoading ? (
                /* Loading State */
                <p className="text-base leading-6 text-neutral-800 tracking-[0.16px]">
                    Loading...
                </p>
            ) : !invoices || invoices.length === 0 ? (
                /* Empty State */
                <div className="flex flex-col gap-8">
                    <p className="text-base leading-6 text-neutral-800 tracking-[0.16px]">
                        No payments have been made on this account yet.
                    </p>
                    <Button
                        variant="primary"
                        size="md"
                        className="w-fit"
                        onClick={() => navigate(`/`)}
                    >
                        Back to Dashboard
                    </Button>
                </div>
            ) : (
                <>
                    {/* Statements List */}
                    <div className="flex flex-col gap-4 mb-8">
                        {invoices.map((invoice, index) => (
                            <Statement
                                key={invoice.idInvoice}
                                idInvoice={invoice.idInvoice}
                                dueDate={invoice.dueDate}
                                total={invoice.total}
                                totalTxFees={invoice.totalTxFees}
                                feeTotal={invoice.feeTotal}
                                totalPaid={invoice.totalPaid}
                                invitations={invoice.invitations}
                                hasFile={!!invoice.hasFile}
                                isExpired={invoice.isExpired === true}
                                isOpen={invoice.open ?? false}
                                onToggle={() => toggle(index)}
                                onDownload={download}
                                statusEnum={Enums.TransactionStatus}
                            />
                        ))}
                    </div>

                    {/* Amount Due Disclaimer */}
                    <div className="mt-auto pt-8">
                        <div className="bg-neutral-50 flex flex-col gap-1 px-4 py-3 rounded-xl text-neutral-800">
                            <h2 className="text-base leading-6 font-medium tracking-[0.16px]">
                                Amount Due
                            </h2>
                            <div className="text-sm leading-5 tracking-[0.14px]">
                                <p className="mb-2">
                                    The Amount Due may not reflect the full amount owed, and may differ from your statement due to updates to your bill, fees, or external payments.
                                </p>
                                <p>
                                    If you have questions about your balance, see your most recent statement or contact the community.
                                </p>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default PaymentHistory;
